<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <!-- PASSWORD -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.current_password.label') }}
                            </label>
                            <div class="password-input">
                                <input v-model="form.current_password" type="password"
                                    class="form-control shadow-none text-black" required
                                    :class="[{ 'is-invalid': submitted && !currentPasswordIsValid }]"
                                    :placeholder="$t('form.current_password.placeholder')" />
                                <i class="ph ph-eye position-absolute top-0 end-0 fs-18 text-muted" v-on:click="togglePassword($event)"></i>
                            </div>
                            <div class="form-messages mt-2 px-1" v-if="errors?.current_password ?? false">
                                <p v-for="error in errors.current_password" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                        error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- PASSWORD -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.password.label') }}
                            </label>
                            <div class="password-input">
                                <input v-model="form.password" type="password" class="form-control shadow-none text-black"
                                    :class="[{ 'is-invalid': submitted && !passwordIsValid }]" required
                                    :placeholder="$t('form.password.placeholder')" />
                                <i class="ph ph-eye position-absolute top-0 end-0 fs-18 text-muted" v-on:click="togglePassword($event)"></i>
                            </div>
                            <div class="form-messages mt-2 px-1" v-if="errors?.password ?? false">
                                <p v-for="error in errors.password" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- PASSWORD_CONFIRMATION -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.password_confirmation.label') }}
                            </label>
                            <div class="password-input">
                                <input v-model="form.password_confirmation" type="password"
                                    class="form-control shadow-none text-black" required
                                    :class="[{ 'is-invalid': submitted && (!passwordIsValid || !confirmPasswordIsValid) }]"
                                    :placeholder="$t('form.password_confirmation.placeholder')" />
                                <i class="ph ph-eye position-absolute top-0 end-0 fs-18 text-muted" v-on:click="togglePassword($event)"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-12">
                    <div class="d-flex align-items-center justify-content-end" v-if="!loading">
                        <!-- SAVE -->
                        <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                            <DiscardButton v-if="!force" @confirm="$router.push({ path: '/profile' })" />
                        <button
                            class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                            type="submit" v-on:click="setPristine">
                            {{ $t('cta.update_password') }}
                        </button>
                        </div>
                    </div>
                    <SpinnerLoading v-else/>
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
                role="alert" id="errorMessage">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>

    <AlertModal id="alertModal" :title="$t('cta.update_password')" :message="$t('feature.auth.$update_password.success_message')" @confirm="redirect" :dismissible="false"/>
    <AlertModal id="forceModal" :title="$t('cta.update_password')" :message="firstAccess === true ? $t('feature.auth.$update_password.first_access') : $t('feature.auth.$update_password.force_message')" />
</template>
  
<script lang="ts">
import { defineComponent } from "vue";
import AlertModal from "@/components/Widgets/AlertModal.vue";
import { UpdatePassword } from "@/utils/models/user.model";
import API from "@/services/api/api.service";
import { Alert, Modal } from "bootstrap";
import stateStore from "@/utils/store";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import { toggle_password_visibility } from "@/utils/methods/form_utils";

export default defineComponent({
    name: "PatientForm",
    components: {
        AlertModal,
        SpinnerLoading,
        DiscardButton
    },
    data: function() {
        return {
            form: {} as UpdatePassword,
            submitted: false,
            errorMessage: undefined as Alert | undefined,
            errors: undefined as any,
            alertModal: null as Modal | null,
            forceModal: null as Modal | null,
            loading: false,
            force: stateStore.isPasswordExpired(), 
        }
    },
    computed: {
        currentPasswordIsValid(): boolean {
            return !(this.errors?.current_password ?? false);
        },
        passwordIsValid(): boolean {
            return this.form.password?.length != 0 && !(this.errors?.password ?? false);
        },
        confirmPasswordIsValid(): boolean {
            return this.form.password_confirmation?.length != 0 && this.form.password === this.form.password_confirmation;
        },
        firstAccess():boolean {
            return stateStore.user?.info?.password_updated_at === undefined || stateStore.user?.info?.password_updated_at === null;
        }
    },
    mounted() {
        this.errorMessage = new Alert('#errorMessage');
        this.alertModal = new Modal('#alertModal', {});
        this.forceModal = new Modal('#forceModal', {});

        if(this.force) {
            this.forceModal?.show();
        }
    },
    methods: {
        submit(event): void {
            this.submitted = true;
            this.errors = undefined;
            event.preventDefault();
            console.log("SUBMIT");
            if (this.passwordIsValid && this.confirmPasswordIsValid) {
                this.updatePassword();
            }
        },
        updatePassword() {
            this.loading = true;
            API.updatePassword(this.form).then((response) => {
                console.log(response);
                if(response.data.success === true) {
                    this.errors = undefined;
                    this.form = {} as UpdatePassword;
                    this.alertModal?.show();
                    stateStore.updateUserInfo();
                }
            }).catch((error) => {
                this.errors = error.response?.data?.errors;
                this.showError();
            }).finally(() => {
                this.loading = false;
            })
        },
        setPristine() {
            this.submitted = true;
        },
        showError() {
            const error = document.getElementById('errorMessage');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('errorMessage');
            error?.classList.remove('show');
        },
        redirect() {
            this.$router.push('/user-consents');
        },
        togglePassword(event?: Event) {
            toggle_password_visibility(event);
        }
    },
});
</script>